import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Gift, TrendingUp, Award, Percent, Eye, EyeOff } from 'lucide-react';

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    city: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
    acceptTerms: false,
    acceptMarketing: false,
  });

  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation errors
  const errors: { [key: string]: string } = {};

  if (touched.firstName && !formData.firstName) {
    errors.firstName = 'Ovo polje je obavezno';
  }

  if (touched.lastName && !formData.lastName) {
    errors.lastName = 'Ovo polje je obavezno';
  }

  if (touched.email) {
    if (!formData.email) {
      errors.email = 'Ovo polje je obavezno';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Unesite validnu email adresu';
    }
  }

  if (touched.phone) {
    if (!formData.phone) {
      errors.phone = 'Ovo polje je obavezno';
    } else if (!/^[\d\s+()-]{6,}$/.test(formData.phone)) {
      errors.phone = 'Unesite validan broj telefona';
    }
  }

  if (touched.dateOfBirth && !formData.dateOfBirth) {
    errors.dateOfBirth = 'Ovo polje je obavezno';
  }

  if (touched.city && !formData.city) {
    errors.city = 'Ovo polje je obavezno';
  }

  if (touched.password) {
    if (!formData.password) {
      errors.password = 'Ovo polje je obavezno';
    } else if (formData.password.length < 8) {
      errors.password = 'Lozinka mora imati najmanje 8 karaktera';
    }
  }

  if (touched.confirmPassword) {
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Ovo polje je obavezno';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Lozinke se ne poklapaju';
    }
  }

  if (touched.acceptTerms && !formData.acceptTerms) {
    errors.acceptTerms = 'Morate prihvatiti pravila programa';
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleBlur = (field: string) => {
    setTouched({ ...touched, [field]: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = {
      firstName: true,
      lastName: true,
      email: true,
      phone: true,
      dateOfBirth: true,
      city: true,
      password: true,
      confirmPassword: true,
      acceptTerms: true,
    };
    setTouched(allTouched);

    // Check if there are any errors
    const hasErrors =
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ||
      !formData.phone ||
      !/^[\d\s+()-]{6,}$/.test(formData.phone) ||
      !formData.dateOfBirth ||
      !formData.city ||
      !formData.password ||
      formData.password.length < 8 ||
      !formData.confirmPassword ||
      formData.password !== formData.confirmPassword ||
      !formData.acceptTerms;

    if (hasErrors) {
      return;
    }

    // Simulate registration
    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setIsSubmitting(false);

    // Navigate to dashboard
    navigate('/go2-legende/dashboard');
  };

  return (
    <div className="bg-gray-50 pt-32 pb-12">
      {/* Hero Section */}
      <div
        className="relative bg-cover bg-center py-16 md:py-24"
        style={{
          backgroundImage: 'url(/assets/go22.jpg)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/60"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left Column - Benefits (hidden on mobile) */}
              <div className="hidden md:block text-white">
                <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                  Postani deo Go2 Legende!
                </h1>
                <p className="font-script text-go2-yellow text-3xl italic mb-8 transform -rotate-2">
                  od putnika do legende
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-go2-pink rounded-full flex items-center justify-center">
                      <Gift className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1">Bonus dobrodošlice</h3>
                      <p className="text-gray-200">
                        Dobij početne bodove odmah po registraciji
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-go2-blue rounded-full flex items-center justify-center">
                      <TrendingUp className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1">Sakupljaj bodove</h3>
                      <p className="text-gray-200">
                        1 EUR = 1 poen na svakom putovanju
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-go2-yellow rounded-full flex items-center justify-center">
                      <Award className="w-6 h-6 text-gray-900" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1">Otključaj nivoe</h3>
                      <p className="text-gray-200">
                        Putnik → Svetski putnik → Legenda
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-go2-pink rounded-full flex items-center justify-center">
                      <Percent className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1">Do 40% popusta</h3>
                      <p className="text-gray-200">
                        Koristi bodove za popust na aranžmane
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Registration Form */}
              <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 border border-gray-100">
                <div className="text-center mb-8">
                  <img
                    src="/assets/go2-logo.png"
                    alt="Go2 Logo"
                    className="h-12 mx-auto mb-6"
                  />
                  <h2 className="text-3xl font-bold text-go2-pink">Kreiraj nalog</h2>
                </div>

                {/* Google Sign-up */}
                <button
                  type="button"
                  onClick={() => navigate('/go2-legende/dashboard')}
                  className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-full py-3 px-4 hover:bg-gray-50 transition-colors mb-4"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="font-medium text-gray-700">Registruj se sa Google</span>
                </button>

                {/* Divider */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-sm text-gray-400">ili sa email-om</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                {/* DEV-only demo quick-fill */}
                {import.meta.env.DEV && (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({
                        firstName: 'Aleksa',
                        lastName: 'Petrović',
                        email: 'aleksa@go2travelling.net',
                        phone: '+381 63 123 4567',
                        dateOfBirth: '1997-05-15',
                        city: 'Beograd',
                        password: 'Demo1234!',
                        confirmPassword: 'Demo1234!',
                        referralCode: 'GO2FRIEND',
                        acceptTerms: true,
                        acceptMarketing: true,
                      });
                      setTouched({});
                    }}
                    className="w-full text-xs text-go2-blue border border-go2-blue/30 rounded-lg py-2 mb-4 hover:bg-blue-50 transition-colors"
                  >
                    Popuni demo podatke
                  </button>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* First Name + Last Name */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        Ime
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        onBlur={() => handleBlur('firstName')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                      />
                      {errors.firstName && (
                        <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Prezime
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        onBlur={() => handleBlur('lastName')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                      />
                      {errors.lastName && (
                        <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email adresa
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={() => handleBlur('email')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="+381 6x xxx xxxx"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={() => handleBlur('phone')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                    )}
                  </div>

                  {/* Date of Birth + City */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-1">
                        Datum rođenja
                      </label>
                      <input
                        type="date"
                        id="dateOfBirth"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        onBlur={() => handleBlur('dateOfBirth')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                      />
                      {errors.dateOfBirth && (
                        <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                        Grad
                      </label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        placeholder="Beograd"
                        value={formData.city}
                        onChange={handleChange}
                        onBlur={() => handleBlur('city')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                      />
                      {errors.city && (
                        <p className="text-red-500 text-sm mt-1">{errors.city}</p>
                      )}
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      Lozinka
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        onBlur={() => handleBlur('password')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Potvrda lozinke
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        onBlur={() => handleBlur('confirmPassword')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
                    )}
                  </div>

                  {/* Referral Code (Optional) */}
                  <div>
                    <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700 mb-1">
                      Referral kod <span className="text-gray-400">(opciono)</span>
                    </label>
                    <input
                      type="text"
                      id="referralCode"
                      name="referralCode"
                      placeholder="Unesite kod prijatelja (opciono)"
                      value={formData.referralCode}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-go2-pink focus:border-go2-pink outline-none transition-all"
                    />
                  </div>

                  {/* Terms Checkbox */}
                  <div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="acceptTerms"
                        checked={formData.acceptTerms}
                        onChange={handleChange}
                        onBlur={() => handleBlur('acceptTerms')}
                        className="mt-1 w-4 h-4 text-go2-pink border-gray-300 rounded focus:ring-go2-pink"
                      />
                      <span className="text-sm text-gray-700">
                        Prihvatam{' '}
                        <Link
                          to="/pravila"
                          target="_blank"
                          className="text-go2-pink hover:text-brand-600 font-medium"
                        >
                          Pravila programa
                        </Link>{' '}
                        i{' '}
                        <Link
                          to="/pravila"
                          target="_blank"
                          className="text-go2-pink hover:text-brand-600 font-medium"
                        >
                          Politiku privatnosti
                        </Link>
                      </span>
                    </label>
                    {errors.acceptTerms && (
                      <p className="text-red-500 text-sm mt-1">{errors.acceptTerms}</p>
                    )}
                  </div>

                  {/* Marketing Checkbox */}
                  <div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="acceptMarketing"
                        checked={formData.acceptMarketing}
                        onChange={handleChange}
                        className="mt-1 w-4 h-4 text-go2-pink border-gray-300 rounded focus:ring-go2-pink"
                      />
                      <span className="text-sm text-gray-700">
                        Želim da primam obaveštenja o novim ponudama i akcijama
                      </span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-go2-pink text-white px-8 py-4 rounded-full font-bold text-base uppercase hover:bg-brand-600 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Registracija...' : 'Registruj se'}
                  </button>
                </form>

                {/* Login Link */}
                <div className="text-center mt-6">
                  <p className="text-sm text-gray-600">
                    Već imaš nalog?{' '}
                    <Link
                      to="/prijava"
                      className="text-go2-pink hover:text-brand-600 font-medium"
                    >
                      Prijavi se
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
